import Phaser from 'phaser';
import { audioManager } from '@/systems/AudioManager';
import { calculateStars } from '@/systems/ScoreSystem';
import { saveSystem } from '@/systems/SaveSystem';
import type { PerTableStats } from '@/types';
import { addBackdrop } from '@/ui/background';
import { Button } from '@/ui/components/Button';
import { Palette, Typography } from '@/ui/theme';
import { fadeInScene, fadeToScene } from '@/utils/transitions';

export interface ResultPayload {
  /** Concluiu sem ficar sem vidas. */
  completed: boolean;
  score: number;
  correctCount: number;
  wrongCount: number;
  livesLost: number;
  speedBonusRate: number;
  totalQuestions: number;
  /** Dados extras para alimentar o SaveSystem. */
  totalCorrectResponseTimeMs: number;
  maxCombo: number;
  perTable: Record<number, PerTableStats>;
  /** Identificador da fase para starsPerLevel e relays. */
  levelKey?: string;
  /** Para o "Tentar de novo" relançar exatamente a mesma fase. */
  worldId?: number;
  levelId?: string;
  levelName?: string;
}

const STAR_FILLED = '★';
const STAR_EMPTY = '☆';

/**
 * ResultScene — tela pós-fase com estrelas, score, ganho de XP e CTAs.
 *
 * Tom positivo mesmo sem vidas (CLAUDE.md §7). Persiste o resultado via
 * SaveSystem.recordSession() em background — a UI estática aparece
 * imediatamente; o `+XP` e o anúncio de rank up entram por animação
 * quando a persistência resolve.
 */
export class ResultScene extends Phaser.Scene {
  private payload!: ResultPayload;
  private xpLabel?: Phaser.GameObjects.Text;
  private rankUpLabel?: Phaser.GameObjects.Text;

  constructor() {
    super({ key: 'ResultScene' });
  }

  init(data: ResultPayload): void {
    this.payload = data;
  }

  create(): void {
    addBackdrop(this);
    fadeInScene(this);
    const { width, height } = this.scale;
    const stars = calculateStars(this.payload);

    this.add
      .text(width / 2, height * 0.16, this.headlineFor(stars), {
        fontFamily: Typography.fontFamily,
        fontSize: '56px',
        color: Palette.textPrimaryCss,
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    this.renderStars(width / 2, height * 0.34, stars);

    this.add
      .text(width / 2, height * 0.5, `${this.payload.score} pontos`, {
        fontFamily: Typography.fontFamily,
        fontSize: '48px',
        color: Palette.accentCss,
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    this.add
      .text(width / 2, height * 0.57, this.statsLine(), {
        fontFamily: Typography.fontFamily,
        fontSize: '20px',
        color: Palette.textMutedCss,
      })
      .setOrigin(0.5);

    // Placeholders para +XP e rank up — preenchidos quando a sessão for
    // persistida (logo abaixo dos pontos).
    this.xpLabel = this.add
      .text(width / 2, height * 0.64, '', {
        fontFamily: Typography.fontFamily,
        fontSize: '24px',
        color: Palette.successCss,
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setAlpha(0);

    this.rankUpLabel = this.add
      .text(width / 2, height * 0.7, '', {
        fontFamily: Typography.fontFamily,
        fontSize: '28px',
        color: Palette.warningCss,
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setAlpha(0);

    new Button(this, width / 2 - 160, height * 0.86, 'Tentar de novo', {
      width: 280,
      height: 64,
      fontSize: 22,
      onClick: () => this.restartLevel(),
    });

    new Button(this, width / 2 + 160, height * 0.86, 'Voltar ao mapa', {
      width: 280,
      height: 64,
      fontSize: 22,
      fillColor: 0x2a2a4a,
      hoverColor: Palette.accent,
      onClick: () => this.backToMap(),
    });

    void this.persistSession(stars);
  }

  private restartLevel(): void {
    if (this.payload.worldId !== undefined && this.payload.levelId !== undefined) {
      fadeToScene(this, 'GameScene', { worldId: this.payload.worldId, levelId: this.payload.levelId });
    } else {
      fadeToScene(this, 'GameScene');
    }
  }

  private backToMap(): void {
    if (this.payload.worldId !== undefined) {
      fadeToScene(this, 'LevelMapScene', { worldId: this.payload.worldId });
    } else {
      fadeToScene(this, 'MenuScene');
    }
  }

  private async persistSession(stars: 0 | 1 | 2 | 3): Promise<void> {
    try {
      const outcome = await saveSystem.recordSession({
        scoreGained: this.payload.score,
        correctCount: this.payload.correctCount,
        wrongCount: this.payload.wrongCount,
        totalCorrectResponseTimeMs: this.payload.totalCorrectResponseTimeMs,
        maxCombo: this.payload.maxCombo,
        perTable: this.payload.perTable,
        levelKey: this.payload.levelKey,
        stars,
      });

      if (!this.scene.isActive()) return; // cena já foi trocada — ignora

      if (outcome.xpGained > 0 && this.xpLabel) {
        this.xpLabel.setText(`+${outcome.xpGained} XP`);
        this.tweens.add({
          targets: this.xpLabel,
          alpha: 1,
          y: this.xpLabel.y - 10,
          duration: 420,
          ease: 'Cubic.easeOut',
        });
      }

      if (outcome.rankUp && this.rankUpLabel) {
        this.rankUpLabel.setText(`Subiu de rank! ${outcome.rankAfter.name}`);
        this.tweens.add({
          targets: this.rankUpLabel,
          alpha: 1,
          scale: { from: 0.7, to: 1 },
          duration: 480,
          delay: 280,
          ease: 'Back.easeOut',
        });
        // Atrasamos o som um pouco para sincronizar com a entrada do label.
        this.time.delayedCall(280, () => audioManager.playSfx('rankUp'));
      }
    } catch (err) {
      console.error('[ResultScene] Falha ao persistir sessão', err);
    }
  }

  private renderStars(centerX: number, y: number, stars: 0 | 1 | 2 | 3): void {
    const total = 3;
    const spacing = 96;
    const startX = centerX - ((total - 1) * spacing) / 2;

    for (let i = 0; i < total; i++) {
      const filled = i < stars;
      const star = this.add
        .text(startX + i * spacing, y, filled ? STAR_FILLED : STAR_EMPTY, {
          fontFamily: Typography.fontFamily,
          fontSize: '88px',
          color: filled ? Palette.warningCss : Palette.textDimCss,
          fontStyle: 'bold',
        })
        .setOrigin(0.5);

      if (filled) {
        // Pop-in sequencial — feedback de progresso, não de pressão.
        star.setScale(0);
        const delay = 200 + i * 220;
        this.tweens.add({
          targets: star,
          scale: 1,
          duration: 380,
          delay,
          ease: 'Back.easeOut',
        });
        // SFX sincronizado com a entrada de cada estrela.
        const starIndex = i;
        this.time.delayedCall(delay, () => audioManager.playSfx('star', { starIndex }));
      }
    }
    // Após a última estrela, fecha com a fanfarra de fase concluída.
    if (stars > 0) {
      const finalDelay = 200 + (stars - 1) * 220 + 380;
      this.time.delayedCall(finalDelay, () => audioManager.playSfx('levelComplete'));
    }
  }

  private headlineFor(stars: 0 | 1 | 2 | 3): string {
    if (!this.payload.completed) return 'Quase lá!';
    if (stars === 3) return 'Perfeito, geek!';
    if (stars === 2) return 'Mandou muito bem!';
    return 'Boa, fase concluída!';
  }

  private statsLine(): string {
    const { correctCount, totalQuestions, speedBonusRate } = this.payload;
    const speedPct = Math.round(speedBonusRate * 100);
    return `${correctCount}/${totalQuestions} corretas · ${speedPct}% no modo turbo`;
  }
}
